import { useState, useRef } from "react"
import { Control, Controller } from "react-hook-form"
import { CountryType, countryData } from "@letshang/assets"
import CountriesFlag from "@letshang/assets/countries_flags.json"
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber"

interface PhoneInputProps {
  control: Control<any>
  name: string
  onBlur: () => void
  value: string
  onChange: (value: string) => void
  inputRef: (el: HTMLTextAreaElement | null) => void
}

const PhoneInput = ({
  control,
  name,
  onBlur,
  value,
  onChange,
  inputRef,
}: PhoneInputProps) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryType>(
    countryData[4],
  ) // Default to US
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const [phoneDisplayed, setPhoneDisplayed] = useState("")
  const phoneUtil = PhoneNumberUtil.getInstance()

  const getFormattedPhone = (digits: string) => {
    try {
      const inputNumber = String(digits || "").replace(/[^\d+]/g, "")
      if (!inputNumber) {
        setPhoneDisplayed(digits || "")
        onChange(digits || "")
        return
      }

      if (!selectedCountry || !selectedCountry.code) {
        setPhoneDisplayed(digits || "")
        onChange(digits || "")
        return
      }

      // Parse the number
      const parsedNumber = phoneUtil.parse(inputNumber, selectedCountry.code)

      // Format the number in NATIONAL format
      let formattedDisplay = phoneUtil.format(
        parsedNumber,
        PhoneNumberFormat.NATIONAL,
      )

      // Remove leading '0' if present
      if (formattedDisplay.startsWith("0")) {
        formattedDisplay = formattedDisplay.replace(/^0/, "")
      }

      setPhoneDisplayed(formattedDisplay)
      onChange(inputNumber) // Store the raw number
    } catch (error) {
      setPhoneDisplayed(digits || "")
      onChange(digits || "")
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const digits = e.target.value
    // Check if last entered character is a number
    const lastChar = digits.slice(-1)
    if (lastChar && !/^\d$/.test(lastChar)) {
      return
    }
    getFormattedPhone(digits)
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        {/* Country Flag */}
        <div
          className="flex items-center justify-center w-14 h-14 bg-white/10 rounded-md cursor-pointer"
          onClick={() => setIsSelectOpen(!isSelectOpen)}
        >
          <img
            alt="Country Flag"
            className="w-6 h-6 self-center"
            src={
              CountriesFlag[
                selectedCountry.code?.toLocaleLowerCase() as keyof typeof CountriesFlag
              ]
            }
          />
        </div>
        {/* Phone Input */}
        <div className="flex-1 bg-white/10 h-14 rounded-md flex items-center px-4 gap-2">
          <span className="text-white">{selectedCountry.dial_code}</span>
          <textarea
            ref={inputRef}
            value={phoneDisplayed}
            onChange={handlePhoneChange}
            onBlur={onBlur}
            placeholder="Enter phone number"
            style={{ resize: "none" }}
            className="flex-1 text-md bg-transparent focus:outline-none focus:ring-0 placeholder-[#C2C2C280] text-white pt-4 h-14"
          />
        </div>
      </div>

      {isSelectOpen && (
        <div className="mt-2 w-full bg-white/10 border border-white/10 rounded-md max-h-48 overflow-y-auto">
          {countryData.map((country) => (
            <div
              key={country.code}
              className="flex items-center gap-2 p-2 hover:bg-white/20 cursor-pointer"
              onClick={() => {
                setSelectedCountry(country)
                setIsSelectOpen(false)
                // Re-format the existing number with new country code
                if (phoneDisplayed) {
                  getFormattedPhone(phoneDisplayed.replace(/[^\d]/g, ""))
                }
              }}
            >
              <img
                src={
                  CountriesFlag[
                    country.code?.toLocaleLowerCase() as keyof typeof CountriesFlag
                  ]
                }
                alt={country.name}
                className="w-6 h-6"
              />
              <span className="text-white text-sm">
                {country.dial_code} {country.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default PhoneInput
